/**
 * 마크다운 텍스트 포맷팅 유틸리티
 */

import { convertLatexToUnicode } from './latexUtils';

/**
 * 인라인 마크다운 요소를 HTML로 변환합니다.
 * @param {string} text - 변환할 텍스트
 * @returns {string} HTML로 변환된 텍스트
 */
export const processInlineMarkdown = (text) => {
    // 먼저 LaTeX 수식을 Unicode로 변환
    let processedText = convertLatexToUnicode(text);

    // 일반 URL 감지
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // arXiv ID 패턴 감지 (링크가 아닌 형태로 적힌 경우도 감지)
    const arxivRegex = /arxiv\.org\/abs\/(\d+\.\d+)|(?:^|\s)(\d{4}\.\d{5})(?:$|\s)/g;

    // 굵은 텍스트 처리 (**text** 형식만 처리)
    const boldRegex = /\*\*(.+?)\*\*/g;

    // 기울임체 텍스트 처리 (_text_ 형식)
    const underscoreItalicRegex = /_(.*?)_/g;

    // 굵은 텍스트 처리 (**text** 패턴) - 가장 먼저 처리
    processedText = processedText.replace(boldRegex, (match, text) => {
        return `<b>${text}</b>`;
    });

    // 단일 별표 굵은 텍스트 처리 (*text* 패턴) - **처리 후** 남은 단일 별표만 매칭
    // Slack에서 온 텍스트(*bold*)도 웹에서 정상 렌더링되도록 처리
    const singleBoldRegex = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g;
    processedText = processedText.replace(singleBoldRegex, (match, text) => {
        return `<b>${text}</b>`;
    });

    // 밑줄 기울임체 처리 (_text_ 패턴)
    processedText = processedText.replace(underscoreItalicRegex, (match, text) => {
        return `<i>${text}</i>`;
    });

    // 일반 URL을 링크로 변환
    processedText = processedText.replace(urlRegex, url =>
        `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-800">${url}<span class="ml-1 text-xs text-gray-500">↗</span></a>`
    );

    // arXiv ID 패턴을 링크로 변환
    processedText = processedText.replace(arxivRegex, (match, id1, id2) => {
        const arxivId = id1 || id2;
        if (match.includes('arxiv.org')) {
            // 이미 URL 형태로 있는 경우
            return `<a href="https://arxiv.org/abs/${arxivId}" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-800">${match}<span class="ml-1 text-xs text-gray-500">↗</span></a>`;
        } else {
            // 숫자 패턴만 있는 경우
            return ` <a href="https://arxiv.org/abs/${arxivId}" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-800">${arxivId}<span class="ml-1 text-xs text-gray-500">↗</span></a> `;
        }
    });

    return processedText;
};

/**
 * MM:SS 타임스탬프를 초 단위로 변환합니다.
 */
const timestampToSeconds = (ts) => {
    const [min, sec] = ts.split(':').map(Number);
    return min * 60 + sec;
};

/**
 * 마크다운 콘텐츠를 React 엘리먼트로 변환합니다.
 * @param {string} content - 마크다운 콘텐츠
 * @param {Object} options - 옵션 (youtubeUrl 등)
 * @returns {Array} React 엘리먼트 배열
 */
export const formatContent = (content, options = {}) => {
    // 문단 단위로 먼저 분리 (Slack 방식과 동일)
    // 연속된 빈 줄은 하나의 문단 구분으로 처리
    const paragraphs = content.split(/\n\n+/);

    return paragraphs.map((paragraph, paragraphIdx) => {
        // 빈 문단은 간격으로 처리
        if (!paragraph.trim()) {
            return <div key={`p-${paragraphIdx}`} className="h-4"></div>;
        }

        // 문단 내의 줄들을 처리
        const lines = paragraph.split('\n');
        const lineElements = lines.map((line, lineIdx) => {
            const idx = `${paragraphIdx}-${lineIdx}`;

            if (line.indexOf('논문 제목:') >= 0)
                line = "";

            // 빈 줄 처리 (문단 내의 단일 줄바꿈)
            if (!line.trim()) {
                return <div key={idx} className="h-2"></div>;
            }

            // 먼저 인라인 마크다운 처리
            const processedLine = processInlineMarkdown(line);

            // 헤더 처리
            if (line.startsWith('#')) {
                // # 개수 확인
                let hashCount = 0;
                for (let i = 0; i < line.length; i++) {
                    if (line[i] === '#') {
                        hashCount++;
                    } else {
                        break;
                    }
                }

                // #뒤에 텍스트 추출
                const text = line.substring(hashCount).trim();
                const level = Math.min(hashCount, 6); // h1-h6까지만 지원

                // 헤더 레벨에 따른 스타일 클래스
                const headerClasses = {
                    1: 'text-2xl font-bold mt-6 mb-3',
                    2: 'text-xl font-bold mt-5 mb-2',
                    3: 'text-lg font-semibold mt-4 mb-2',
                    4: 'text-base font-semibold mt-3 mb-1',
                    5: 'text-sm font-semibold mt-2 mb-1',
                    6: 'text-sm font-medium mt-2 mb-1',
                };

                // YouTube 타임스탬프 헤더 처리: ## [MM:SS ~ MM:SS] 소제목
                if (options.youtubeUrl && level === 2) {
                    const tsMatch = text.match(/^\[(\d{1,2}:\d{2}) ~ (\d{1,2}:\d{2})\] (.+)$/);
                    if (tsMatch) {
                        const startTs = tsMatch[1];
                        const endTs = tsMatch[2];
                        const title = tsMatch[3];
                        const seconds = timestampToSeconds(startTs);
                        const separator = options.youtubeUrl.includes('?') ? '&' : '?';
                        const linkUrl = `${options.youtubeUrl}${separator}t=${seconds}`;
                        return (
                            <div key={idx} className={headerClasses[2]}>
                                <a href={linkUrl} target="_blank" rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                    dangerouslySetInnerHTML={{ __html: `▶ [${startTs} ~ ${endTs}] ${processInlineMarkdown(title)}` }} />
                            </div>
                        );
                    }
                }

                return (
                    <div key={idx} className={headerClasses[level] || headerClasses[6]}
                        dangerouslySetInnerHTML={{ __html: processInlineMarkdown(text) }} />
                );
            }

            // 숫자로 시작하는 논문 제목 패턴 처리 (1. ..., 2. ... 등)
            const numberedTitleMatch = line.trim().match(/^(\d+)\.\s+(.+)$/);
            if (numberedTitleMatch) {
                const number = numberedTitleMatch[1];
                const title = numberedTitleMatch[2];

                return (
                    <div key={idx} className="flex items-start my-3">
                        <span className="inline-block font-bold text-xl mr-2 flex-shrink-0">{number}.</span>
                        <span className="font-semibold text-lg" dangerouslySetInnerHTML={{ __html: processInlineMarkdown(title) }}></span>
                    </div>
                );
            }

            // 리스트 아이템 처리
            // 리스트 아이템 패턴 확인: 마커(-,*,+,•,.) 뒤에 공백이 있어야 함
            const listMatch = line.trim().match(/^([-*+•\.])\s+(.+)$/);
            if (listMatch) {
                // 시작 공백 계산
                let indentCount = 0;
                for (let i = 0; i < line.length; i++) {
                    if (line[i] === ' ' || line[i] === '\t') {
                        indentCount++;
                    } else {
                        break;
                    }
                }

                // 텍스트 추출 (마커와 그 뒤 공백 제외)
                const text = listMatch[2];

                // 리스트 아이템을 일관된 구조로 렌더링
                return (
                    <div key={idx} className="flex items-start my-1" style={{ marginLeft: `${indentCount * 0.25}rem` }}>
                        <span className="inline-block w-5 flex-shrink-0 text-center">•</span>
                        <span dangerouslySetInnerHTML={{ __html: processInlineMarkdown(text) }}></span>
                    </div>
                );
            }

            // 한 줄에 점(.) 하나만 있는 경우 간격 조정을 위한 특별 처리
            if (line.trim() === '•' || line.trim() === '.') {
                return <div key={idx} className="my-2"></div>;
            }

            // 들여쓰기 처리
            const indentMatch = line.match(/^(\s+)/);
            const paddingLeft = indentMatch ? indentMatch[1].length * 0.5 : 0;

            // 일반 텍스트는 이미 processInlineMarkdown으로 처리됨
            return (
                <div key={idx} style={{ paddingLeft: `${paddingLeft}rem` }}
                    dangerouslySetInnerHTML={{ __html: processedLine }} />
            );
        });

        // 문단을 div로 감싸서 문단 간 간격 확보
        return (
            <div key={`p-${paragraphIdx}`} className="mb-4">
                {lineElements}
            </div>
        );
    });
};

/**
 * AI 카테고리를 필터링합니다.
 * @param {Array} categories - 카테고리 배열
 * @returns {Array} 필터링된 카테고리 배열
 */
export const filterAICategory = (categories) => {
    // 카테고리가 하나만 있고 그것이 AI 카테고리라면 그대로 반환
    if (categories.length === 1) {
        return categories;
    }

    // 카테고리가 여러 개라면 "Artificial Intelligence (cs.AI)" 제거
    return categories.filter(cat => cat !== "Artificial Intelligence (cs.AI)");
};
